import React from 'react';
import { Button } from '../Button/Button';
import { Badge } from '../Badge/Badge';
import { Dropdown } from '../Dropdown/Dropdown';
import { Checkbox } from '../Checkbox/Checkbox';
import type { DropdownItemProps } from '../Dropdown/Dropdown.types';
import type { MasterCrudField, MasterCrudAction } from './MasterCrud.types';
import { CalendarIcon, Bars3Icon, LinkIcon } from './icons';

interface MasterCRUDCardProps<T> {
  data: T;
  fields: MasterCrudField<T>[];
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  rowActions?: (record: T, index: number) => React.ReactNode;
  index?: number;
  actions?: MasterCrudAction<T>[];
  renderCardContent?: (data: T, index: number) => React.ReactNode;
  /** Habilitar selección múltiple */
  enableMultiSelect?: boolean;
  /** Si la tarjeta está seleccionada actualmente      */
  isSelected?: boolean;
  /** Callback cuando cambia la selección */
  onSelect?: (selected: boolean) => void;
  /** Función de traducción inyectada por el padre       */
  t: (key: string, defaultValue?: string) => string;
}

/**
 * MasterCRUDCard - Componente de tarjeta para la vista de grid
 */
export const MasterCRUDCard = <T extends Record<string, unknown>>({
  data,
  fields,
  onEdit,
  onDelete,
  rowActions,
  actions = [],
  renderCardContent,
  index = 0,
  enableMultiSelect = false,
  isSelected = false,
  onSelect,
  t,
}: MasterCRUDCardProps<T>) => {
  // ===== FILTRAR CAMPOS VISIBLES EN GRID =====
  const visibleFields = fields.filter(field => {
    // Sincronización obligatoria con el modo tabla (showInList)
    if (field.config?.showInList !== undefined) return field.config.showInList;
    // Fallbacks para retrocompatibilidad
    if (field.config?.showInGrid !== undefined) return field.config.showInGrid;
    if (field.config?.visibleInTable !== undefined) return field.config.visibleInTable;
    return true;
  });

  // ===== IDENTIFICAR TÍTULO Y FOOTER (Para estructura de Card) =====
  // El título es el primero marcado como 'title' o el primer campo de texto visible
  const titleField = visibleFields.find(f => f.config?.cardPosition === 'title') || visibleFields.find(f => f.type === 'text');

  // El footer es el primero marcado como 'footer' o el primer campo de fecha visible
  const footerField = visibleFields.find(f => f.config?.cardPosition === 'footer') || visibleFields.find(f => f.type === 'date');

  // El resto de los campos que NO son el título principal ni el footer
  const contentFields = visibleFields.filter(f => f !== titleField && f !== footerField);

  // ===== RENDERIZAR VALOR =====
  const renderValue = (field: MasterCrudField<T>) => {
    const value = data[field.accessorKey as string];
    if (value === null || value === undefined) return null;

    if (field.renderCell) {
      return field.renderCell(value, data, 0);
    }

    if (field.type === 'select') {
      const option = field.options?.find(opt => opt.value === value);
      return option?.label || value;
    }

    if (field.type === 'lookup' && field.lookupConfig) {
      // Si el valor es un objeto (dato populado), intentar mostrar los displayFields
      if (typeof value === 'object' && value !== null) {
        const display = field.lookupConfig.displayFields
          .map(k => (value as any)[k])
          .filter(v => v !== undefined && v !== null)
          .join(' - ');
        return display || String(value);
      }
    }

    return String(value);
  };

  // ===== CONSTRUIR OPCIONES DEL DROPDOWN =====
  const linkAction = actions.find(a => a.id === 'link');
  const otherActions = actions.filter(a => a.id !== 'link');

  const dropdownItems: DropdownItemProps[] = otherActions.map((action) => {
    const isDisabled = typeof action.disabled === 'function' ? action.disabled(data) : action.disabled;
    const actionName = typeof action.name === 'function' ? action.name(data) : action.name;
    const actionIcon = typeof action.icon === 'function' ? action.icon(data) : action.icon;
    
    return {
      children: actionName,
      icon: actionIcon,
      disabled: isDisabled,
      onClick: () => action.action?.(data),
      className: actionName.toLowerCase().includes('eliminar') || actionName.toLowerCase().includes('delete')
        ? 'text-error-content hover:text-error-content dark:text-error-content'
        : '',
    };
  });

  // Legacy fallback (si no hay actions pero sí onEdit/onDelete, aunque MasterCrud.tsx siempre manda actions)
  if (dropdownItems.length === 0) {
    if (onEdit) {
      dropdownItems.push({
        children: t('edit', 'Editar'),
        icon: <span className="w-4 h-4">✎</span>, // Placeholder si no hay icono a mano, pero MasterCrud.tsx manda actions
        onClick: () => onEdit(data),
      });
    }

    if (onDelete) {
      dropdownItems.push({
        children: t('delete', 'Eliminar'),
        icon: <span className="w-4 h-4">🗑️</span>,
        onClick: () => onDelete(data),
        className: 'text-error-content hover:text-error-content dark:text-error-content',
      });
    }
  }

  // ===== ESTILOS BASE (Mapeados de Figma a Tailwind/Proyecto) =====
  // Container: bg-white border border-border-primary rounded-2xl p-5 gap-1
  const containerClasses = `
    group
    relative
    flex
    flex-col
    gap-1
    p-5
    rounded-2xl
    shadow-sm
    hover:shadow-md
    transition-all
    duration-200
    ${isSelected
      ? 'bg-[#f7fcff] dark:bg-primary-900/20 border-primary-600 ring-1 ring-primary-600 dark:border-primary-600 dark:ring-primary-600'
      : 'bg-white dark:bg-dark-bg-primary border-border-primary dark:border-dark-border-primary'}
  `.replace(/\s+/g, ' ').trim();

  // ===== RENDERIZAR CHECKBOX DE SELECCIÓN =====
  const selectionCheckbox = enableMultiSelect && (
    <div className="absolute top-5 left-5 z-10">
      <Checkbox
        checked={isSelected}
        onChange={(e) => onSelect?.(e.target.checked)}
        aria-label={t('selectRecord', 'Seleccionar registro')}
      />
    </div>
  );

  // ===== CONTENIDO POR DEFECTO =====
  const defaultContent = (
    <>
      {/* HEADER: Title & Actions */}
      <div className={`flex items-start justify-between gap-2 w-full ${enableMultiSelect ? 'pl-8' : ''}`}>
        {/* Title & Code */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <h3 className="text-[18px] font-medium text-content-primary dark:text-dark-content-primary truncate leading-[20px]">
            {titleField ? (renderValue(titleField) as React.ReactNode) : t('noTitle', 'Sin título')}
          </h3>
          {/* Si hay un campo de fecha o similar que actúe como código en el header */}
          {visibleFields.filter(f => f.config?.cardPosition === 'subtitle').length === 0 && footerField && (
            <p className="text-[14px] font-normal italic text-content-secondary dark:text-content-tertiary truncate leading-[18px]">
              {renderValue(footerField) as React.ReactNode}
            </p>
          )}
        </div>

        {/* Actions Dropdown */}
        <div className="flex items-center gap-1 absolute top-1 right-1">
          {linkAction && (
            <Button
              type="outline"
              size="xs"
              iconOnly
              leftIcon={typeof linkAction.icon === 'function' ? linkAction.icon(data) : linkAction.icon}
              onClick={() => linkAction.action?.(data)}
              ariaLabel={typeof linkAction.name === 'function' ? linkAction.name(data) : linkAction.name}
              title={typeof linkAction.name === 'function' ? linkAction.name(data) : linkAction.name}
              className="shrink-0"
            />
          )}
          {(dropdownItems.length > 0 || rowActions) && (
            <Dropdown
              renderTrigger={() => (
                <button
                  type="button"
                  className="flex items-center justify-center p-2 rounded-lg text-content-tertiary hover:text-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-inverse/10 transition-all duration-150"
                  aria-label={t('moreOptions', 'Más opciones')}
                >
                  <Bars3Icon className="w-5 h-5" />
                </button>
              )}
              items={dropdownItems}
            />
          )}
        </div>
      </div>

      {/* ÁREA DE CONTENIDO (Siguiendo el orden definido en el array fields) */}
      <div className={`flex flex-col gap-1 w-full ${enableMultiSelect ? 'pl-8' : ''}`}>
        {contentFields.map(field => {
          const value = data[field.accessorKey as string];
          if (value === null || value === undefined) return null;

          // 1. Campos de Estado / Booleanos (Renderizados como Badges)
          if (field.config?.cardPosition === 'status' || (field.type === 'boolean' && !field.config?.cardPosition)) {
            const trueLabel = field.config?.trueLabel || t('active', 'Activo');
            const falseLabel = field.config?.falseLabel || t('inactive', 'Inactivo');
            return (
              <div key={field.accessorKey as string} className="mt-1">
                <Badge
                  color={field.type === 'boolean' ? (value ? 'green' : 'zinc') : 'blue'}
                  label={field.type === 'boolean' ? (value ? trueLabel : falseLabel) : String(value)}
                />
              </div>
            );
          }

          // 2. Subtítulos (Texto resaltado en azul Siesa)
          if (field.config?.cardPosition === 'subtitle') {
            return (
              <p key={field.accessorKey as string} className="text-[16px] font-medium text-primary-600 dark:text-primary-500 truncate leading-[20px]">
                {renderValue(field) as React.ReactNode}
              </p>
            );
          }

          // 3. Campos de cuerpo estándar (Texto base)
          return (
            <p key={field.accessorKey as string} className="text-[14px] font-normal text-content-primary dark:text-dark-content-primary truncate leading-[20px]">
              {renderValue(field) as React.ReactNode}
            </p>
          );
        })}
      </div>

      {/* FOOTER */}
      {footerField && (
        <div className={`flex items-center gap-2 mt-2 pt-4 border-t border-border-secondary dark:border-dark-border-primary w-full ${enableMultiSelect ? 'pl-8' : ''}`}>
          <CalendarIcon className="w-4 h-4 text-content-secondary dark:text-content-secondary" />
          <p className="text-[12px] font-medium italic text-content-secondary dark:text-content-secondary truncate leading-[14px]">
            {renderValue(footerField) as React.ReactNode}
          </p>
        </div>
      )}
    </>
  );

  // ===== CONTENIDO PERSONALIZADO (con dropdown de acciones) =====
  const customContent = renderCardContent && (
    <div className={enableMultiSelect ? 'pl-8' : ''}>
      {/* Actions Dropdown (esquina superior derecha) */}
      {(dropdownItems.length > 0 || rowActions) && (
        <div className="absolute top-1 right-1 z-10">
          <Dropdown
            renderTrigger={() => (
              <button
                type="button"
                className="flex items-center justify-center p-2 rounded-lg text-content-tertiary hover:text-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-inverse/10 transition-all duration-150"
                aria-label={t('moreOptions', 'Más opciones')}
              >
                <Bars3Icon className="w-5 h-5" />
              </button>
            )}
            items={dropdownItems}
          />
        </div>
      )}

      {/* Contenido personalizado */}
      {renderCardContent(data, index)}
    </div>
  );

  return (
    <div className={containerClasses}>
      {selectionCheckbox}
      {renderCardContent ? customContent : defaultContent}
    </div>
  );
};
